import { Check, ChevronRight } from "lucide-react";
import { PrizeText } from "@/components/prize-text";
import { cn } from "@/lib/utils";

interface NotificationProps {
  title: string;
  /** Sits inline before the title — e.g. "Task complete" ahead of the task.
   *  On a win it is an overline above the reward instead ("You won"), so the
   *  reward keeps the card's whole width. */
  label?: string;
  text?: string;
  imageUrl?: string;
  /** `prize`/`task` promote the title to the card's focal point — used when the
   *  title IS the thing that happened (a reward, "25 Free Spins", or the task
   *  the player finished, "Deposit €500"), with its label beside it and the
   *  supporting line underneath. */
  variant?: "default" | "prize" | "task";
  /** When set, the whole card becomes a button and grows a chevron affordance.
   *  Left out for purely informational notifications. */
  onClick?: () => void;
  /** A card that stays until it is acted on (shown with `duration: Infinity`):
   *  no time-left bar, which would run out on a card that never leaves. */
  sticky?: boolean;
}

/**
 * A win takes the whole card: solid `--prizes` gold surface, dark text, content
 * centered — unmistakably a different kind of event than the informational
 * cards, with no chip competing for attention inside the row. Dark-on-gold is
 * 11.9:1; gold *text* on the light card was 1.35:1, which is why the colour
 * always goes on a fill, never on type.
 *
 * A finished task keeps the light card and marks the task name with a
 * `--primary` tinted chip (tint, not solid — a saturated block plus the arrow
 * read heavy), one type-step below the label: the label says what happened, the
 * chip carries the detail.
 *
 * Everything that must flip together when the surface flips (label, title,
 * secondary line, arrow disc, progress bar) lives in one entry, so a variant
 * can't end up half-recoloured.
 */
const VARIANT = {
  default: {
    card: "bg-foreground border-background/15",
    center: false,
    label: "text-background/70",
    title: "text-sm font-bold text-background",
    text: "text-background/80",
    disc: "border border-primary/50 bg-primary/25",
    chevron: "text-background",
    bar: "bg-primary",
  },
  task: {
    card: "bg-foreground border-background/15",
    center: false,
    label: "text-background/70",
    title:
      "truncate rounded-md bg-primary/25 px-1.5 py-px text-xs leading-tight font-medium text-background",
    text: "text-background/80",
    disc: "border border-primary/50 bg-primary/25",
    chevron: "text-background",
    bar: "bg-primary",
  },
  prize: {
    card: "bg-prizes border-background/15",
    center: true,
    label: "text-background/75",
    // The reward never truncates (CLAUDE.md): `PrizeText` keeps it on one line,
    // shrinking it first and gliding over what still doesn't fit.
    title: "text-base leading-tight font-bold text-background",
    text: "text-background/80",
    // The disc inverts on gold: a dark fill with a gold glyph (11.9:1), since a
    // primary-tinted disc would vanish into the surface.
    disc: "bg-background",
    chevron: "text-prizes",
    bar: "bg-background/40",
  },
} as const;

/**
 * The notification card. Placement, stacking, timing and swipe-to-dismiss are
 * sonner's job (see `Toaster`) — this only draws the card, so it renders the
 * same wherever it is mounted.
 */
export const Notification = ({
  title,
  label,
  text,
  imageUrl,
  variant = "default",
  onClick,
  sticky = false,
}: NotificationProps) => {
  const styles = VARIANT[variant];

  const content = (
    <>
      <div className="size-12 shrink-0 overflow-hidden rounded-2xl bg-primary">
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="size-full object-cover"
            aria-hidden="true"
          />
        )}
      </div>
      {/* A win centers its content (`flex-1` + centered items) — the card is
          the celebration, not a log line. The other variants stay left-aligned
          reading rows. */}
      <div
        className={cn(
          "flex min-w-0 flex-col justify-center gap-0.5 pr-2 pl-2",
          styles.center
            ? "flex-1 items-center text-center"
            : "items-start text-left"
        )}
      >
        {variant === "prize" ? (
          <>
            {/* The reward is the card: "You won" rides above it as an
                overline, and the reward takes the full width on one line. */}
            {label && (
              <span
                className={cn(
                  "font-heading text-xs leading-tight font-medium",
                  styles.label
                )}
              >
                {label}
              </span>
            )}
            <PrizeText
              size="inherit"
              weight="bold"
              tone="inherit"
              fit="marquee"
              className={cn("max-w-full", styles.title)}
            >
              {title}
            </PrizeText>
          </>
        ) : (
          <div className="flex min-w-0 items-center gap-1.5">
            {label && (
              <span
                className={cn(
                  "flex shrink-0 items-center gap-1 font-heading text-sm font-medium",
                  styles.label
                )}
              >
                {/* A finished task earns a tick. It inherits the label's colour
                    rather than taking `--active` green, which is only 1.9:1 on
                    this card — under the 3:1 that a meaningful icon needs. */}
                {variant === "task" && (
                  <Check
                    strokeWidth={3}
                    className="size-3.5"
                    aria-hidden="true"
                  />
                )}
                {label}
              </span>
            )}
            <div className={cn("font-heading", styles.title)}>{title}</div>
          </div>
        )}
        {text && (
          <div className={cn("font-heading text-xs font-medium", styles.text)}>
            {text}
          </div>
        )}
      </div>
      {/* Figma 3499:3674 draws this disc at 5% of the dark token, which is all
          but invisible on the light card — so per-variant fills instead, with
          the glyph carrying the contrast (see VARIANT). */}
      {onClick && (
        <div
          className={cn(
            "ml-auto flex size-7 shrink-0 items-center justify-center rounded-full",
            styles.disc
          )}
        >
          <ChevronRight
            strokeWidth={2.5}
            className={cn("size-4", styles.chevron)}
          />
        </div>
      )}
      {/* Time left before this one dismisses itself. Decorative to a screen
          reader — the toast is announced on arrival, and a countdown it can't
          act on is noise. Keyframes + hover-pause live in index.css.

          `--toast-duration` comes from the Toaster, which sets it from the same
          constant it hands sonner as `duration` — no fallback here on purpose,
          so the length can never disagree with the actual dismiss. Without the
          variable the animation is invalid and never runs, which is why the
          resting state is scaleX(0): no countdown, no bar (a card rendered
          outside a Toaster isn't on a timer anyway). */}
      {!sticky && (
        <span
          aria-hidden="true"
          data-toast-progress
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 h-1 origin-left [transform:scaleX(0)] [animation:toast-progress_var(--toast-duration)_linear_forwards]",
            styles.bar
          )}
        />
      )}
    </>
  );

  const className = cn(
    "pointer-events-auto relative flex w-full flex-row items-center gap-1 px-2 py-2",
    // `overflow-hidden` so the progress bar is clipped to the card's radius
    // instead of poking out of the rounded corners.
    "overflow-hidden rounded-3xl",
    // Surface per variant. The hairline is what keeps two stacked toasts
    // reading as two cards — sonner's stack leaves only ~20px of the one behind
    // showing, and card-on-card without an edge looks like one slab.
    "border",
    styles.card,
    // Figma 3499:3666's two-layer shadow rather than the shared
    // `.notification-shadow` (which the prize-reveal window + campaign layout
    // also use, so it can't be retuned for this card alone). The ambient layer
    // is at 0.40 instead of the design's 0.66: at full strength the front
    // card's shadow falls across the card *behind* it and muddies the whole
    // stack into grey.
    "shadow-[0px_13px_12px_0px_rgba(15,22,29,0.22),0px_8px_24px_0px_rgba(7,11,15,0.40)]"
  );

  if (!onClick) {
    return <div className={cn(className, "select-none")}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        className,
        "cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
      )}
    >
      {content}
    </button>
  );
};
